/**
 * Range configuration for a RAG color level.
 */
export interface RagRange {
  /** Minimum value for this range (inclusive) */
  min?: number
  /** Maximum value for this range (inclusive) */
  max?: number
}

/**
 * RAG (Red-Amber-Green) threshold configuration options.
 */
export interface RagThresholds {
  /** Green (success) range configuration */
  green?: RagRange
  /** Amber (warning) range configuration */
  amber?: RagRange
}

/**
 * RAG severity result type.
 */
export type RagSeverity = 'success' | 'warning' | 'danger'

/**
 * Determines the RAG (Red-Amber-Green) severity based on a value and threshold values.
 * 
 * Simple API: Pass two thresholds (amber and green) for the common case.
 * - If value >= greenThreshold: Green (success)
 * - If value >= amberThreshold && value < greenThreshold: Amber (warning)
 * - If value < amberThreshold: Red (danger)
 * 
 * @param value - The numeric value to evaluate
 * @param amberThreshold - The threshold for amber (warning) - values below this are danger
 * @param greenThreshold - The threshold for green (success) - values at or above this are green
 * @returns The severity level: 'success' (green), 'warning' (amber), or 'danger' (red)
 * 
 * @example
 * // Higher is better: Green >= 95, Amber 90-94, Red < 90
 * getRagSeverity(96, 90, 95)
 * // Returns: 'success'
 * 
 * @example
 * // Value 92: Amber (between 90 and 94)
 * getRagSeverity(92, 90, 95)
 * // Returns: 'warning'
 * 
 * @example
 * // Value 85: Danger (below 90)
 * getRagSeverity(85, 90, 95)
 * // Returns: 'danger'
 */
export function getRagSeverity(value: number, amberThreshold: number, greenThreshold: number): RagSeverity
/**
 * Determines the RAG (Red-Amber-Green) severity based on a value and configurable thresholds.
 * 
 * Advanced API: Pass a thresholds object for complex ranges (e.g., lower is better, custom ranges).
 * 
 * @param value - The numeric value to evaluate
 * @param thresholds - Threshold configuration object with green and/or amber ranges
 * @returns The severity level: 'success' (green), 'warning' (amber), or 'danger' (red)
 * 
 * @example
 * // Lower is better: Green <= 5, Amber 6-10, Red > 10
 * getRagSeverity(7, { green: { max: 5 }, amber: { min: 6, max: 10 } })
 * // Returns: 'warning'
 * 
 * @example
 * // Range with both min and max: Green 80-90, Amber 70-79, Red otherwise
 * getRagSeverity(85, { green: { min: 80, max: 90 }, amber: { min: 70, max: 79 } })
 * // Returns: 'success'
 */
export function getRagSeverity(value: number, thresholds: RagThresholds): RagSeverity
export function getRagSeverity(value: number, amberThresholdOrThresholds: number | RagThresholds, greenThreshold?: number): RagSeverity {
  // Simple API: two number parameters
  if (typeof amberThresholdOrThresholds === 'number' && greenThreshold !== undefined) {
    const amberThreshold = amberThresholdOrThresholds

    if (value >= greenThreshold) {
      return 'success'
    } else if (value >= amberThreshold) {
      return 'warning'
    } else {
      return 'danger'
    }
  }

  // Advanced API: thresholds object
  const thresholds = amberThresholdOrThresholds as RagThresholds
  const { green, amber } = thresholds

  // Check if value is within green range
  if (green) {
    const { min, max = Infinity } = green

    if (min !== undefined && max !== Infinity) {
      // Both min and max specified - check range
      if (value >= min && value <= max) {
        return 'success'
      }
    } else if (min !== undefined) {
      // Only min specified - value must be >= min
      if (value >= min) {
        return 'success'
      }
    } else if (max !== Infinity) {
      // Only max specified - value must be <= max
      if (value <= max) {
        return 'success'
      }
    }
  }

  // Check if value is within amber range (only if not already green)
  if (amber) {
    const { min, max } = amber

    if (min !== undefined && max !== undefined) {
      // Both min and max specified - check range
      if (value >= min && value <= max) {
        return 'warning'
      }
    } else if (min !== undefined) {
      // Only min specified - value must be >= min
      if (value >= min) {
        return 'warning'
      }
    } else if (max !== undefined) {
      // Only max specified
      if (green?.min !== undefined) {
        // If green.min exists, amber is values >= max and < green.min
        // This creates a range just below green
        if (value >= max && value < green.min) {
          return 'warning'
        }
      } else {
        // No green.min, so any value <= max is amber
        if (value <= max) {
          return 'warning'
        }
      }
    }
  }

  // Default to red if no thresholds are met
  return 'danger'
}

